/**
 * El copy de la sección de la página web (GKTIBO-65, 135, 136).
 *
 * Vive aparte de `textos.ts` por tamaño (D4), y porque es el único bloque del
 * alta que traduce un vocabulario del sistema —los motivos del rastreador— al
 * idioma del dueño. Las dos tablas de abajo se anclan a los tipos reales
 * (`satisfies`): agregar un motivo sin su traducción deja de compilar, en vez
 * de aparecer como jerga en producción (F16).
 */

import type { MotivoDeDescarte, MotivoDeFalla } from '@chatbot/shared-types';

export const web = {
  titulo: '¿Tenés una página web?',
  ayuda: 'Si la tenés, la leemos para saber qué cuenta de tu negocio.',
  etiqueta: 'La dirección de tu página',
  ejemplo: 'https://mi-negocio.com',
  leer: 'Leer mi página',
  noTengo: 'No tengo página web',
  // Declinar no es un error: se dice en positivo y se sigue.
  sinWeb: 'Perfecto. Contanos vos abajo qué hace tu negocio.',

  // Mientras corre dice QUÉ está haciendo. Un spinner de treinta segundos
  // se lee como «se colgó».
  leyendo: (leidas: number) =>
    leidas === 0 ? 'Entrando a tu página…' : `Leímos ${leidas} páginas de tu sitio…`,
  listo: (leidas: number) =>
    leidas === 1 ? 'Leímos 1 página de tu sitio.' : `Leímos ${leidas} páginas de tu sitio.`,
  // Se leyó parte: decirlo, para no dar a entender que se leyó todo.
  listoHayMas: (leidas: number) =>
    `Leímos ${leidas} páginas de tu sitio. Hay más, pero con esto alcanza para empezar.`,
  // Respondió, y no había nada que leer. Anunciarlo como «leímos 0 páginas»
  // con un «listo» al lado es contar un fracaso como un logro.
  sinNada: 'Entramos a tu página pero no encontramos texto que podamos leer.',
  otraDireccion: 'Probar con otra dirección',
  // El rastreo quedó colgado. Se dice, y se ofrece salida: quedarse mirando
  // un cartel que no cambia es peor que un error.
  tardaDemasiado:
    'Tu página está tardando más de lo normal. Podés seguir escribiendo abajo mientras tanto.',
  // NO dice «revisá abajo»: abajo todavía no aparece nada de lo leído. Armar
  // el `.md` con eso es TASK-05 (GKTIBO-68), y prometerlo antes es
  // exactamente el patrón de «el bot promete lo que no puede cumplir».
  revisar: 'Contanos abajo lo que tu página no dice: qué NO hacés, y cuándo querés que te avise.',
} as const;

/**
 * Por qué no se pudo leer, en el idioma del dueño (regla D6).
 *
 * Mostrarle `sin-texto-util` sería nuestra jerga en su pantalla. Y ninguno
 * de estos textos lo culpa: la página no está mal, es nuestro lector el que
 * no llega.
 */
export const porQueNoSePudo = {
  'sin-texto-util':
    'Tu página arma su contenido con un programa que no podemos leer desde acá. ' +
    'No es un error tuyo: contanos abajo qué hace tu negocio y listo.',
  'sin-respuesta':
    'No pudimos entrar a tu página. Fijate que la dirección esté bien escrita, ' +
    'o contanos abajo y seguimos.',
  'destino-bloqueado':
    'Esa dirección no lleva a tu página. Revisá que sea la dirección pública de tu sitio.',
  'dominio-no-declarado': 'Esa no parece una dirección de página web. Revisala y probá de nuevo.',
  'robots-lo-prohibe': 'Tu página pide que no la leamos automáticamente, y lo respetamos.',
  'no-es-html': 'Esa dirección no es una página web. Si es un documento, subilo abajo.',
  otro: 'No pudimos leer tu página. Contanos abajo qué hace tu negocio y seguimos.',
  // El tipo se ancla al vocabulario real (`MotivoDeFalla`): agregar un
  // motivo sin su traducción deja de compilar, en vez de aparecer como un
  // texto genérico en producción (F16).
} satisfies Record<MotivoDeFalla, string> & { otro: string };

/**
 * Por qué no se pudo ni empezar.
 *
 * Son fallas **nuestras**, no de la dirección que escribió. Contárselas
 * todas como «tu URL está mal» lo manda a corregir en loop algo que estaba
 * bien (F15, L40 — en la pantalla, no sólo en el log).
 */
export const noSePudoEmpezar = {
  url: 'Esa no parece una dirección de página web. Revisala y probá de nuevo.',
  nuestro: 'No pudimos arrancar la lectura. No es por tu dirección; probá de nuevo en un rato.',
  sesion: 'Tu sesión venció. Volvé a entrar y seguimos donde quedaste.',
} as const;

/**
 * Qué se leyó y qué no, y pedir más (GKTIBO-136).
 *
 * Los motivos del descarte no son fallas —la página está bien— sino
 * decisiones nuestras, y se cuentan como tales: «es el blog», no «error».
 */
export const paginas = {
  queLeimos: (n: number) => (n === 1 ? 'Qué leímos (1 página)' : `Qué leímos (${n} páginas)`),
  queDejamosAfuera: (n: number) =>
    n === 1 ? 'Qué dejamos afuera (1 página)' : `Qué dejamos afuera (${n} páginas)`,
  motivos: {
    'otro-idioma': 'está en otro idioma',
    despriorizada: 'es el blog, un caso de cliente o una página legal',
    tope: 'llegamos al tope de páginas',
  } satisfies Record<MotivoDeDescarte, string>,
  // Se dice cuántas puede pedir ANTES de que se apaguen las casillas: un
  // checkbox gris sin explicación es un bug a los ojos de quien lo ve.
  elegi: (max: number) => `Marcá las que querés que leamos también, hasta ${max} por vez.`,
  leerEstas: (n: number) => (n === 0 ? 'Leer estas también' : `Leer estas también (${n})`),
  leyendo: 'Leyendo las que pediste…',
  // Un pedido que no se pudo encolar se DICE, con su motivo, y no con el de
  // «tu dirección está mal»: la dirección estaba bien (revisión, I1).
  rastreoEnCurso: 'Todavía estamos leyendo tu página. Esperá un momento y volvé a pedir.',
  noSePudoPedir: 'No pudimos pedir esas páginas. Probá con menos, o más tarde.',
  // Lo pedido no se pudo leer: lo que ya se había leído sigue ahí (m5).
  noSePudoLeerLoPedido:
    'No pudimos leer las páginas que pediste. Lo que ya habíamos leído sigue guardado.',
  // Pidió tres y se leyeron dos: se dice (m8).
  noSeLeyeron: (n: number) =>
    n === 1
      ? 'Una de las páginas que pediste no se pudo leer.'
      : `${n} de las páginas que pediste no se pudieron leer.`,
} as const;
